/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

/**
 * Valid restrictions for UppyDashboard while uploading.
 */
export enum UppyAllowedFileTypes {
  AUDIO = 'audio/*',
  IMAGE = 'image/*',
  IMAGE_EXTENSION = '.jpg',
  VIDEO = 'video/*',
  ANY = '*/*',
}

/**
 * Options required to configure uppy.
 * Ex: Used in `uppy-dashboard.service` to type passed in options for open()
 * and initializeUppy.
 */
export type BulbConfigurableOptions = {
  allowedFileTypes: UppyAllowedFileTypes[] | string[];
  assetLibrary: boolean;
  embed: boolean;
  multiSelect: boolean;
  folderId?: string;
  uploadState?: string;
  contentBlockData?: any;
  useAdobeExpressPlugin?: boolean;
  useCanvaPlugin?: boolean;
  orientation?: string;
};

/**
 * Type defining the file restrictions for configuring uppy.
 * Ex: Used to define default file restrictions in uppy-config and
 * to type params in `uppy-config.service` for getConfigWithRestrictions()
 */
export type BulbFileRestrictions = {
  maxNumberOfFiles?: number;
  allowedFileTypes?: UppyAllowedFileTypes[];
  autoProceed?: boolean;
};

/**
 * Upload request interface for Images, Videos, Audio, and attached files through uppy companion/ dashboard.
 */
export interface CompanionUploadRequest {
  accountId: string
  fileContentType: string
  fileExtension: string
  fileName: string
  uniqueId: string
  fileSize: number
  source: string,
  folderId?: string
  width?: number
  height?: number
  videoLengthInMillis?: number
}

/**
 * The event types for UppyComponentEventStatus.
 */
export enum UppyComponentEventType {
  ON_SUCCESS = 'SUCCESS',
  ON_FAILURE = 'FAILURE',
  ON_COMPLETE = 'COMPLETE',
}

/**
 * The event statuses types for UppyComponentEventStatus.
 */
export enum UppyComponentEventStatus {
  SUCCESS = 'SUCCESS',
  PARTIAL = 'PARTIAL',
  FAILURE = 'FAILURE',
  PROCESSING_SUCCESS = 'PROCESSING_SUCCESS',
  PROCESSING_FAILURE = 'PROCESSING_FAILURE',
}

/**
 * The event emitted from the Uppy Component.
 */
export interface UppyComponentEvent {
  // Where the event was emitted from in the Uppy Component.
  type: UppyComponentEventType

  // The event status. The event could be triggered from 'OnSuccess' but be a fail on the call to bulb for example.
  // Or the OnComplete with multiple uploads could succeed on 2 of the 3 and fail on the last.
  status: UppyComponentEventStatus

  // The response body, if it exists.
  response?: CompanionUploadResponse

  // The file object of the failed file upload, if it exists
  file?: any,

  // The error message, if it exists.
  error?: any,

  // Success object if there is no error
  success?: CompanionSuccessfulResponse

  // state of asset.
  uploadState?: any

  // content block data.
  contentBlockData?: any
}

/**
 * The response object from uploading to the companion endpoint.
 * See AssetLibraryEntryResponseDTO.java
 */
export interface CompanionUploadResponse {
  entry: any
  usage: any
  used: boolean
  nextIndex? : number
  hasMoreFiles? : boolean
  uploadedAssets?: string[]
  meta: any
}

/**
 * This is currently unused as companion response is too dynamic, and they don't have their own types file.
 * Currently leaving this here a reference for what we can expect from Companion on a success and how we use it.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface CompanionSuccessfulResponse {
  name: string
  extension: string
  size: number
  data: {
    custom: {
      imageHeight?: number
      imageWidth?: number
      imageRotation?: number
      videoHeight?: number
      videoWidth?: number
      videoDurationMillis: string
    }
  }
  meta: {
    'content-type': string
    key: string
  }
}
